<template>
  <div id="account-partner">
    <v-card class="pl-8 pr-10 pt-8 pb-8 mt-4 mb-5 elevation-0">
      <div v-if="type === 'referral'" class="mb-8">
        <h1 class="card-title">
          <v-icon size="16" color="secondary" class="account-partner-title-icon">
            mdi-gift-outline
          </v-icon>
          {{ $t(`views.myAccount.partner.invite.title`) }}
        </h1>
        <v-card
          class="account-partner-description pa-5 mb-0 mt-2 elevation-0"
          color="softBackgroundGrey"
        >
          <ul>
            <li>{{ $t(`views.myAccount.partner.invite.li1`) }}</li>
            <li>{{ $t(`views.myAccount.partner.invite.li2`) }}</li>
            <li>{{ $t(`views.myAccount.partner.invite.li3`) }}</li>
          </ul>
        </v-card>
      </div>

      <div class="d-flex flex-wrap">
        <div class="mr-12 mb-5" @click="copyItem(userCode)">
          <h1 class="card-title mb-3">{{ $t(`views.myAccount.partner.code.${type}`) }}</h1>
          <v-chip
            :color="$vuetify.theme.dark ? 'dark_backgroundDarkBlue' : 'softBackgroundGrey'"
            class="account-partner-chip"
            label
          >
            <span class="partner-url">{{ userCode }}</span>
            <v-divider class="mx-4" vertical light></v-divider>
            <span>
              <v-icon size="16">mdi-content-copy</v-icon>
              <span class="mx-1">{{ $t('common.copy') }}</span>
            </span>
          </v-chip>
        </div>

        <div @click="copyItem(partnerUrl)">
          <h1 class="card-title mb-3">{{ $t('views.myAccount.partner.link') }}</h1>
          <v-chip
            :color="$vuetify.theme.dark ? 'dark_backgroundDarkBlue' : 'softBackgroundGrey'"
            class="account-partner-chip"
            label
          >
            <span class="partner-url">{{ partnerUrl }}</span>
            <v-divider class="mx-4" vertical light></v-divider>
            <span>
              <v-icon size="16">mdi-content-copy</v-icon>
              <span class="mx-1">{{ $t('common.copy') }}</span>
            </span>
          </v-chip>
        </div>
      </div>
    </v-card>

    <v-card class="pl-8 pr-10 pt-8 pb-10 mt-4 mb-5 elevation-0">
      <h1 class="card-title mb-3">{{ $t('views.myAccount.partner.referredUsers') }}</h1>

      <v-data-table
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="totalItems"
        sort-by="createdAt"
        :sort-desc="true"
        class="account-partner-users-table elevation-0 mt-2"
        mobile-breakpoint="0"
        :loading="loading"
        :footer-props="{
          'items-per-page-options': defaultPaginationItemsPerPageOptions,
        }"
        :items-per-page="20"
        dense
        @update:options="loadItems"
      >
        <template v-slot:item.status="{ item }">
          <span
            v-if="
              ['PLAN_PREMIUM', 'PLAN_PRO', 'PLAN_UNLIMITED', 'PLAN_CUSTOM'].indexOf(item.status) >
              -1
            "
          >
            <span class="status-subscription">
              {{ $t(`views.myAccount.billing.status.${item.status}`) }}
            </span>
          </span>
          <span v-else>
            {{ $t(`views.myAccount.billing.status.${item.status}`) }}
          </span>
        </template>

        <template v-slot:item.interval="{ item }">
          {{ item.interval ? $t(`views.myAccount.partner.interval.${item.interval}`) : '' }}
        </template>

        <template v-slot:item.createdAt="{ item }">
          {{ getDateUserFullFormat(item.createdAt) }}
        </template>

        <template v-slot:item.lastSignIn="{ item }">
          {{
            isInLast24h(item.lastSignIn)
              ? $t(`views.myAccount.partner.lastSignInLast24h`)
              : getDateUserFormat(item.lastSignIn)
          }}
        </template>

        <template v-slot:item.referralCompleted="{ item }">
          <span v-if="item.referralCompleted">
            <v-chip small color="softBackgroundGreen">
              {{ $t(`views.myAccount.partner.referralCompleted.completed`) }}
            </v-chip>
          </span>
          <span v-else>
            <v-chip small color="softBackgroundGrey">
              {{ $t(`views.myAccount.partner.referralCompleted.pending`) }}
            </v-chip>
          </span>
        </template>
      </v-data-table>
    </v-card>

    <v-card v-if="type === 'partner'" class="pl-8 pr-10 pt-8 pb-10 mt-4 mb-5 elevation-0">
      <v-card-title>
        <h1 class="card-title mb-3">{{ $t('views.myAccount.partner.payments') }}</h1>

        <v-spacer />

        <v-menu
          v-model="paymentDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          min-width="290px"
          content-class="elevation-1"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="paymentDateRangeText"
              :label="$t('views.common.commonFilters.filter.dateRange')"
              readonly
              dense
              hide-details
              style="max-width: 230px"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker v-model="paymentDates" no-title range first-day-of-week="1" />
        </v-menu>
      </v-card-title>

      <v-data-table
        :headers="paymentHeaders"
        :items="paymentTableItems"
        :options.sync="paymentOptions"
        :server-items-length="paymentTotalItems"
        sort-by="date"
        :sort-desc="true"
        class="account-partner-payments-table elevation-0 mt-2"
        mobile-breakpoint="0"
        :loading="paymentLoading"
        :footer-props="{
          'items-per-page-options': defaultPaginationItemsPerPageOptions,
        }"
        :items-per-page="20"
        dense
        :item-class="getPaymentItemClass"
        @update:options="loadPaymentItems"
      >
        <template v-slot:item.date="{ item }">
          <b v-if="item._totals">Total</b>
          <span v-else>{{ getDateUserFullFormat(item.date) }}</span>
        </template>
        <template v-slot:item.amount="{ item }">
          {{ item.amount != null ? item.amount.toFixed(2) + ' €' : '' }}
        </template>
        <template v-slot:item.referrerToReceive="{ item }">
          {{ item.referrerToReceive != null ? item.referrerToReceive.toFixed(2) + ' €' : '' }}
        </template>
      </v-data-table>
    </v-card>

    <v-card v-if="type === 'referral'" class="referral-partner pa-7 pb-3 mb-0 mt-10 elevation-0">
      <div>
        <!-- <v-icon
          size="19"
          :color="$vuetify.theme.dark ? 'white' : 'secondary'"
          class="referral-partner-title-icon"
        >
          mdi-cash
        </v-icon> -->
        <span
          class="referral-partner-title"
          :class="{ 'white--text': $vuetify.theme.dark, 'secondary--text': !$vuetify.theme.dark }"
        >
          {{ $t(`views.myAccount.partner.referralPartner.title`) }}
        </span>
      </div>
      <p class="referral-partner-description mt-1">
        {{ $t(`views.myAccount.partner.referralPartner.description`) }}
        <a
          target="_blank"
          href="https://www.filios.app/contacta"
          :class="{ 'white--text': $vuetify.theme.dark, 'secondary--text': !$vuetify.theme.dark }"
        >
          {{ $t(`views.myAccount.partner.referralPartner.contact`) }}
        </a>
      </p>
    </v-card>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { DateTime } from 'luxon'
import { getReferredUsers, getPartnerStripePayments } from '@/lib/api.js'
import tableMixin from '@/views/components/core/tableMixin.js'
import { getDateUserFullFormat, getDateUserFormat, isInLast24h } from '@/lib/utils/date.js'

export default {
  components: {},
  mixins: [tableMixin],
  props: {
    type: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      paymentLoading: false,
      paymentItems: [],
      paymentTotalItems: 0,
      paymentOptions: {},
      paymentTotals: null,
      paymentDates: [
        DateTime.now().minus({ days: 29 }).toFormat('yyyy-MM-dd'),
        DateTime.now().toFormat('yyyy-MM-dd'),
      ],
      paymentDateMenu: false,
    }
  },
  computed: {
    ...mapState(['user']),
    userCode() {
      return this.user.code
    },
    partnerUrl() {
      return `https://go.filios.app/#/signup?referralCode=${this.user.code}`
    },
    defaultHeaders() {
      const defaultHeaders = [
        {
          text: this.$t('views.myAccount.partner.createdAt'),
          value: 'createdAt',
        },
        {
          text: this.$t('views.myAccount.partner.lastSignIn'),
          value: 'lastSignIn',
        },
        {
          text: this.$t('views.myAccount.partner.email'),
          value: 'email',
        },
        {
          text: this.$t('views.myAccount.partner.status'),
          value: 'status',
        },
        {
          text: this.$t('views.myAccount.partner.interval.title'),
          value: 'interval',
        },
        {
          text: this.$t('views.myAccount.partner.trialDays'),
          value: 'trialDays',
        },
      ]

      if (this.type === 'referral') {
        defaultHeaders.push({
          text: this.$t('views.myAccount.partner.referralCompleted.title'),
          value: 'referralCompleted',
        })
      }

      return defaultHeaders
    },
    paymentTableItems() {
      if (!this.paymentTotals) return this.paymentItems
      const totalsRow = {
        _totals: true,
        amount: this.paymentTotals.amount,
        referrerToReceive: this.paymentTotals.referrerToReceive,
      }
      return [totalsRow, ...this.paymentItems]
    },
    paymentDateRangeText() {
      if (!this.paymentDates || this.paymentDates.length < 2) return ''
      return this.paymentDates.map((d) => getDateUserFormat(d)).join(' - ')
    },
    paymentHeaders() {
      return [
        { text: this.$t('views.myAccount.partner.payments_date'), value: 'date' },
        { text: this.$t('views.myAccount.partner.email'), value: 'customerEmail' },
        { text: this.$t('views.myAccount.partner.payments_amount'), value: 'amount' },
        {
          text: this.$t('views.myAccount.partner.payments_referrerToReceive'),
          value: 'referrerToReceive',
        },
      ]
    },
  },
  watch: {
    paymentDates: {
      handler(val) {
        if (val.length === 2) {
          const from = DateTime.fromISO(val[0])
          const to = DateTime.fromISO(val[1])
          if (to.diff(from, 'months').months > 6) {
            this.$root.$snackbar.error({
              text: this.$t('views.myAccount.partner.paymentsDateRangeError'),
            })
            return
          }
          this.paymentOptions = { ...this.paymentOptions, page: 1 }
          this.loadPaymentItems()
        }
      },
      deep: true,
    },
  },
  methods: {
    getDateUserFullFormat,
    getDateUserFormat,
    isInLast24h,
    getPaymentItemClass(item) {
      return item._totals ? 'totals-row font-weight-bold' : ''
    },
    async loadItems() {
      this.loading = true
      try {
        const filter = {}
        const { page, limit, sort } = this.getTablePaginationOptions(this.options)
        const response = await getReferredUsers({ filter, page, limit, sort })
        this.setTablePaginatedResponse(response)
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.loading = false
    },
    async loadPaymentItems() {
      this.paymentLoading = true
      try {
        const { page, limit, sort } = this.getTablePaginationOptions(this.paymentOptions)
        const filter = {}
        if (this.paymentDates.length >= 1) filter.dateFrom = this.paymentDates[0]
        if (this.paymentDates.length >= 2) filter.dateTo = this.paymentDates[1]
        const response = await getPartnerStripePayments({ page, limit, sort, filter })
        this.paymentItems = response.data.docs
        this.paymentTotalItems = response.data.totalDocs
        this.paymentTotals = response.data.totals
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.paymentLoading = false
    },
    async copyItem(item) {
      await navigator.clipboard.writeText(item)
      this.$root.$snackbar.success({ text: this.$t('common.copyOk') })
    },
  },
}
</script>

<style lang="scss">
#account-partner {
  .account-partner-chip {
    cursor: pointer;
  }
  h2 {
    font-size: 13px;
    font-weight: 600;
  }
  .account-partner-title-icon {
    margin-bottom: 4px;
    margin-right: 4px;
  }
  .account-partner-description {
    font-size: 13px;
  }
  .status-subscription {
    font-weight: 600;
  }
  .account-partner-payments-table {
    .totals-row {
      background-color: rgba(0, 0, 0, 0.05);
    }
  }
  .referral-partner {
    .referral-partner-title-icon {
      margin-bottom: 2px;
    }
    .referral-partner-title {
      font-size: 13px;
      font-weight: 600;
    }
    .referral-partner-description {
      font-size: 13px;
      font-weight: 300;
    }
  }
}
</style>
